import { Button } from "@/lib/components/Button/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/lib/components/Dialog";
import {
  overlayTemplateQueryKeys,
  useDeleteOverlayTemplateMutation,
  useOverlayTemplatesQuery,
} from "@/lib/queries/overlay-template-queries";
import { OverlayTemplateDto } from "@repo/api/overlays";
import {
  IconAlertCircle,
  IconDots,
  IconInfoCircle,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import AddOrEditOverlayTemplate from "./AddOverlayTemplate";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import Toast from "@/lib/components/Toast";
import { queryClient } from "@/lib/queries/query-client";
import { cn, formatCreatedAt } from "@/lib/utils";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { generateNewElementExternalIds } from "@/lib/overlay-template";
import LoadingSpinner from "@/lib/components/LoadingSpinner";

type Props = {
  children: React.ReactNode;
};

export default function OverlayTemplateDialog({ children }: Props) {
  const [open, setOpen] = useState(false);
  const [currentView, setCurrentView] = useState<"LIST" | "NEW_TEMPLATE">(
    "LIST",
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (open) {
          setCurrentView("LIST");
        }
        setOpen(open);
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className={cn(
          `max-w-lg`,
          currentView === "LIST" && "max-h-[80svh] min-h-100",
        )}
        classNameInner={cn(
          currentView === "LIST" &&
            "flex min-h-0 flex-col gap-0 overflow-y-hidden px-0 py-0 sm:px-0",
        )}
        showCloseButton={currentView !== "LIST"}
      >
        {currentView === "LIST" && (
          <OverlayTemplateList
            onNewTemplate={() => setCurrentView("NEW_TEMPLATE")}
            onClose={() => setOpen(false)}
          />
        )}
        {currentView === "NEW_TEMPLATE" && (
          <AddOrEditOverlayTemplate
            onCancel={() => setCurrentView("LIST")}
            onClose={() => setOpen(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function OverlayTemplateList({
  onNewTemplate,
  onClose,
}: {
  onNewTemplate: () => void;
  onClose: () => void;
}) {
  const { teamExternalId } = useParams();

  const {
    data: templates,
    isPending,
    isError,
    isSuccess,
  } = useOverlayTemplatesQuery(teamExternalId!);

  const sortedTemplates = useMemo(() => {
    return (
      templates?.sort((a, b) => {
        return a.name.localeCompare(b.name);
      }) ?? []
    );
  }, [templates]);

  return (
    <>
      <DialogHeader showCloseButton={true} variant="bordered">
        <DialogTitle>Templates</DialogTitle>
        <DialogDescription>
          Reuse overlays across videos with templates.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-1 flex-col gap-1 overflow-y-auto py-4">
        {isPending && <OverlayTemplateListLoader />}
        {isError && <OverlayTemplateListError />}
        {isSuccess && sortedTemplates.length === 0 && (
          <OverlayTemplateListEmpty />
        )}
        {sortedTemplates.length > 0 &&
          sortedTemplates.map((template) => (
            <OverlayTemplateListItem
              key={template.externalId}
              template={template}
              onClose={onClose}
            />
          ))}
      </div>

      <DialogFooter variant="bordered">
        <Button
          className="w-full"
          variant="outline"
          icon={<IconPlus className="h-4 w-4" strokeWidth={2.5} />}
          onClick={() => {
            onNewTemplate();
          }}
        >
          New template
        </Button>
      </DialogFooter>
    </>
  );
}

function OverlayTemplateListLoader() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <LoadingSpinner />
    </div>
  );
}

function OverlayTemplateListError() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center">
      <IconAlertCircle className="text-error" size={24} />
      <p className="mt-1.5 text-sm text-text-secondary">
        Failed to load templates.
      </p>
    </div>
  );
}

function OverlayTemplateListEmpty() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center [&_svg]:stroke-[url(#icon-gradient)]">
      <IconInfoCircle className="text-primary-400" size={24} />
      <p className="mt-1.5 text-center text-sm font-semibold">
        No existing templates
      </p>
      <p className="mt-0.5 text-center text-sm text-text-secondary">
        Start by creating your first template.
      </p>
    </div>
  );
}

function OverlayTemplateListItem({
  template,
  onClose,
}: {
  template: OverlayTemplateDto;
  onClose: () => void;
}) {
  const addOverlayTemplateToComposition = useEditorStore(
    (state) => state.service.addOverlayTemplateToComposition,
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const showActionsClass = cn(
    "hidden group-focus-within:inline-flex group-hover:inline-flex",
    isMenuOpen && "inline-flex",
  );

  return (
    <div className="h-14 px-5">
      <div
        className="group relative flex h-14 items-center gap-3 rounded-lg px-2"
        tabIndex={0}
      >
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden text-left">
          <h3 className="truncate text-sm font-medium">{template.name}</h3>
          <p className="mt-0.5 truncate text-sm text-text-secondary">
            {formatCreatedAt(template.createdAt, "Created")}
          </p>
        </div>
        <div
          className={cn("flex shrink-0 items-center gap-2", showActionsClass)}
        >
          <OverlayTemplateItemOptionsDropdown
            template={template}
            isMenuOpen={isMenuOpen}
            onMenuOpenChange={setIsMenuOpen}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              addOverlayTemplateToComposition(
                generateNewElementExternalIds(template),
              );
              Toast.info("Template added to video");
              onClose();
            }}
          >
            Add to video
          </Button>
        </div>
      </div>
    </div>
  );
}

type OverlayTemplateItemOptionsDropdownProps = {
  template: OverlayTemplateDto;
  isMenuOpen: boolean;
  onMenuOpenChange: (isOpen: boolean) => void;
};

function OverlayTemplateItemOptionsDropdown({
  template,
  isMenuOpen,
  onMenuOpenChange,
}: OverlayTemplateItemOptionsDropdownProps) {
  const { teamExternalId } = useParams();

  const { mutate: deleteTemplate, isPending } =
    useDeleteOverlayTemplateMutation({
      teamExternalId: teamExternalId!,
      overlayTemplateExternalId: template.externalId,
      onSuccess: () => {
        Toast.info("Template deleted successfully");
      },
      onError: (error) => {
        console.error(error);
        Toast.error("Failed to delete template");
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: overlayTemplateQueryKeys.getOverlayTemplates(
            teamExternalId!,
          ),
        });
      },
    });

  return (
    <DropdownMenu open={isMenuOpen} onOpenChange={onMenuOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="iconSm">
          <IconDots size={20} />
        </Button>
      </DropdownMenuTrigger>
      {isMenuOpen && (
        <DropdownMenuContent
          className="w-56"
          align="start"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();

              if (isPending) return;

              deleteTemplate();
            }}
          >
            <div className="flex items-center gap-2">
              {isPending ? (
                <LoadingSpinner />
              ) : (
                <>
                  <IconTrash size={18} />
                  <span className="text-sm">Delete template</span>
                </>
              )}
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
}
